import { useRef, useState } from 'react';
import { Logo } from '@/trace/icons';
import '@/workspace/workspace.css';
import '@/trace/login.css';
import './auth-controls.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const SITE_URL = 'https://tethertracking.com';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The sign-in screen, after the prototype's login page: the access panel on the left, the product exhibit on the right.

function BrandHeader() {
  return (
    <header className="access-header">
      <a className="login-brand" href={SITE_URL} aria-label="Tether home"><span className="login-brand-mark"><Logo /></span><span>tether</span></a>
      <a className="home-link" href={SITE_URL}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M19 12H5m6-6-6 6 6 6" /></svg>Back to site</a>
    </header>
  );
}

function AccessFooter() {
  return <footer className="access-footer"><span>© {new Date().getFullYear()} Tether</span><span>Clarity looks good on you.</span></footer>;
}

function Exhibit() {
  return (
    <aside className="product-exhibit" aria-labelledby="exhibit-title">
      <div className="exhibit-grain" aria-hidden="true" />
      <div className="exhibit-topline"><span>THE FULL PICTURE</span><span className="exhibit-coordinate">TETHER / 01</span></div>
      <div className="exhibit-heading"><h2 id="exhibit-title">Every touchpoint.<br /><span>One clear story.</span></h2><p>Connect the first click to the final sale.<br />And see everything that made it happen.</p></div>
      <div className="journey-exhibit" role="img" aria-label="Sample journey: a Meta ad, a Google search and an email contribute to a 248-dollar order. Tether connects the touchpoints to the revenue.">
        <div className="exhibit-ruler"><span>SAMPLE CUSTOMER JOURNEY</span><span>3 TOUCHPOINTS → 1 ORDER</span></div>
        <div className="journey-canvas">
          <svg className="journey-lines" viewBox="0 0 600 280" preserveAspectRatio="none" aria-hidden="true">
            <path className="line-base" d="M174 45H222Q236 45 236 59V126Q236 140 250 140H285M174 140H285M174 235H222Q236 235 236 221V154Q236 140 250 140H285M325 140H388" />
            <path className="line-active line-one" pathLength="1" d="M174 45H222Q236 45 236 59V126Q236 140 250 140H285" />
            <path className="line-active line-two" pathLength="1" d="M174 140H285" />
            <path className="line-active line-three" pathLength="1" d="M174 235H222Q236 235 236 221V154Q236 140 250 140H285" />
            <path className="line-active line-four" pathLength="1" d="M325 140H388" />
          </svg>
          <div className="touchpoint touchpoint-one"><span className="touch-icon meta-icon">∞</span><span><strong>Meta Ads</strong><small>The first impression</small></span><i /></div>
          <div className="touchpoint touchpoint-two"><span className="touch-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></svg></span><span><strong>Google Search</strong><small>A little more curious</small></span><i /></div>
          <div className="touchpoint touchpoint-three"><span className="touch-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M3 5h18v14H3zM3 6l9 7 9-7" /></svg></span><span><strong>Email</strong><small>The right reminder</small></span><i /></div>
          <div className="trace-junction"><Logo /></div>
          <div className="order-card"><span className="order-caption"><i />REVENUE, TRACED</span><strong>$248<span>.00</span></strong><div className="order-divider" /><span className="order-bottom"><span>Order #1048</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg></span></div>
        </div>
        <div className="journey-legend"><span><i />Connected by Tether</span><span>Illustrative data</span></div>
      </div>
      <div className="exhibit-caption"><span className="caption-index">01</span><div><strong>See the journey behind the number.</strong><p>One place for your channels, customers, and revenue.</p></div></div>
      <div className="exhibit-footer"><span>ATTRIBUTION, WITHOUT THE GUESSWORK.</span><span className="exhibit-plus" aria-hidden="true">+</span></div>
    </aside>
  );
}

// The shared frame for onboarding, the operator picker and invitations: their cards sit where the sign-in form does.
export function AuthLayout({ children, wide = false, eyebrow = 'Tether workspace' }) {
  return (
    <div className={`login-page sp-auth${wide ? ' sp-auth-wide' : ''}`}>
      <a className="login-skip" href="#login-main">Skip to content</a>
      <main className="login-layout" id="login-main">
        <section className={`access-panel${wide ? ' access-panel-wide' : ''}`}>
          <BrandHeader />
          <div className="access-content">
            <span className="access-eyebrow"><i aria-hidden="true" />{String(eyebrow).toUpperCase()}</span>
            <div className="sp-login-card">{children}</div>
          </div>
          <AccessFooter />
        </section>
        <Exhibit />
      </main>
    </div>
  );
}

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgot, setForgot] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const submitting = useRef(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const validate = () => {
    const errors = {};
    if (!email.trim()) errors.email = 'Enter your work email.';
    else if (!EMAIL.test(email.trim())) errors.email = 'Use an email address like you@company.com.';
    if (!password) errors.password = 'Enter your password.';
    return errors;
  };

  const submit = async e => {
    e.preventDefault();
    if (submitting.current) return;
    const errors = validate();
    setFieldErrors(errors);
    if (errors.email || errors.password) { (errors.email ? emailRef : passwordRef).current?.focus(); return; }
    submitting.current = true;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      await onLogin(data);
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <a className="login-skip" href="#login-main">Skip to login</a>
      <main className="login-layout" id="login-main">
        <section className="access-panel" aria-labelledby="login-title">
          <BrandHeader />
          <div className="access-content">
            <div className="access-intro">
              <span className="access-eyebrow"><i aria-hidden="true" />YOUR NEXT GOOD DECISION</span>
              <h1 id="login-title">Welcome back.</h1>
              <p>Your marketing has a story.<br />Pick up where you left off.</p>
            </div>
            <form className="login-form" id="login-form" noValidate onSubmit={submit} aria-busy={loading} aria-describedby={error ? 'login-error' : undefined}>
              <div className="form-field">
                <label htmlFor="login-email">Email address</label>
                <input ref={emailRef} id="login-email" name="email" type="email" inputMode="email" autoComplete="email" placeholder="you@company.com" value={email}
                  onChange={e => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors(f => ({ ...f, email: '' })); }} disabled={loading} required
                  aria-invalid={!!fieldErrors.email} aria-describedby="email-error" autoFocus />
                <p className="field-error" id="email-error" aria-live="polite">{fieldErrors.email}</p>
              </div>
              <div className="form-field">
                <div className="password-label">
                  <label htmlFor="login-password">Password</label>
                  <button className="text-control" type="button" id="forgot-password" aria-controls="recovery-note" aria-expanded={forgot} onClick={() => setForgot(v => !v)}>Forgot password?</button>
                </div>
                <div className="password-field">
                  <input ref={passwordRef} id="login-password" name="password" type={show ? 'text' : 'password'} autoComplete="current-password" placeholder="Your password" value={password}
                    onChange={e => { setPassword(e.target.value); if (fieldErrors.password) setFieldErrors(f => ({ ...f, password: '' })); }} disabled={loading} required
                    aria-invalid={!!fieldErrors.password} aria-describedby="password-error" />
                  <button className="password-toggle" type="button" id="toggle-password" onClick={() => setShow(v => !v)} disabled={loading} aria-label={show ? 'Hide password' : 'Show password'} aria-pressed={show} aria-controls="login-password">
                    <svg className="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /><path className="eye-slash" d="m4 3 16 18" /></svg>
                  </button>
                </div>
                <p className="field-error" id="password-error" aria-live="polite">{fieldErrors.password}</p>
              </div>
              <div className="recovery-note" id="recovery-note" role="status" hidden={!forgot}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7h.01" /></svg>
                <p>Ask your workspace admin to reset your password — they can send a new invite from Settings → Team.</p>
              </div>
              {error && <p className="field-error login-error" id="login-error" role="alert">{error}</p>}
              <button className="login-submit" type="submit" id="login-submit" disabled={loading}>
                <span>{loading ? 'Signing in…' : 'Log in'}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M5 12h14m-6-6 6 6-6 6" /></svg>
              </button>
              <p className="login-status" id="login-status" role="status" aria-live="polite">{loading ? 'Checking your workspace…' : ''}</p>
              <div className="login-divider"><span /><p>New to Tether?</p><span /></div>
              <a className="demo-entry" href={SITE_URL}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M3 4h18v16H3zM9 4v16M9 9h12" /></svg>Book a demo
                <svg className="demo-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="m8 5 7 7-7 7" /></svg>
              </a>
              <p className="prototype-note">Workspace access is by invitation.<br />Ask your admin for an invite if you don’t have a login yet.</p>
            </form>
          </div>
          <AccessFooter />
        </section>
        <Exhibit />
      </main>
    </div>
  );
}
